import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { t } from '@/shared/lib/i18n'
import LogDetailsDialog from './LogDetailsDialog'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

interface FailureOccurrence {
  id: number
  completedAtLocal: string | null
}

interface Props {
  instrumentId: number
  instrumentLabel: string
  serialNumber: string | null
  date: string | null
  instrumentTaskLogIds: number[]
  /**
   * REBUILD-554: per-occurrence identity (completion time) aligned with instrumentTaskLogIds.
   * Lets each row name the exact failed occurrence so "View Details" opens that occurrence and
   * two failures on the same day are distinguishable instead of bare "Failure #N" rows.
   */
  failures?: FailureOccurrence[]
  fromDate: string | null
  toDate: string | null
  dateToLogIdMap: Record<string, number>
  onClose: () => void
  /**
   * After a successful void from Log Details: parent refreshes calendar/instrument and
   * either closes (no remaining failures) or keeps the Failures modal with remaining ids.
   */
  onAfterDelete?: (deletedLogId: number) => void
}

const InstrumentFailuresDialog = ({
  instrumentId,
  instrumentLabel,
  serialNumber,
  date,
  instrumentTaskLogIds,
  failures = [],
  fromDate,
  toDate,
  dateToLogIdMap,
  onClose,
  onAfterDelete,
}: Props) => {
  const { formatDateTime } = useSiteDateTime()
  const completedAtById = new Map(failures.map((failure) => [failure.id, failure.completedAtLocal]))
  const failureTimeLabel = (logId: number): string | null => {
    const completedAtLocal = completedAtById.get(logId)
    return completedAtLocal ? formatDateTime(completedAtLocal) : null
  }
  // Keep the user's pick in state; derive activeLogId from the prop list so a voided id
  // clears only when the parent drops it (AC4 — no picker flash with stale ids).
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null)
  const activeLogId =
    selectedLogId != null && instrumentTaskLogIds.includes(selectedLogId) ? selectedLogId : null

  const isOpen = date !== null
  const hasLogs = instrumentTaskLogIds.length > 0
  const isSingleFailure = instrumentTaskLogIds.length === 1
  const headerTitle = serialNumber ? `${instrumentLabel} (${serialNumber})` : instrumentLabel

  const handleLogClick = (logId: number) => setSelectedLogId(logId)
  const handleLogClose = () => setSelectedLogId(null)

  // Require isOpen: activeLogId alone must not keep LogDetails mounted after the Failures
  // row modal is cleared (calendar always mounts this component). Parent remounts via key
  // when the modal closes so a stale selectedLogId cannot skip the multi-failure picker.
  if (isOpen && (activeLogId !== null || isSingleFailure)) {
    const logId = activeLogId ?? instrumentTaskLogIds[0]
    const handleAfterDelete = (deletedLogId: number) => {
      // Forward the voided id from Log Details — the day strip can void a different log
      // than `logId`. Drill-down clears when parent drops the id (derived activeLogId).
      onAfterDelete?.(deletedLogId)
    }
    return (
      <LogDetailsDialog
        // Remount when the open failure changes (e.g. multi-failure void leaves one).
        // Otherwise finishVoid's deferred day-list refetch runs on the reused instance and
        // can expand the wrong log after taskLogId flips to the remaining failure.
        key={logId}
        instrumentId={instrumentId}
        instrumentTaskId={null}
        instrumentLabel={instrumentLabel}
        serialNumber={serialNumber}
        taskLogId={logId}
        date={date}
        fromDate={fromDate}
        toDate={toDate}
        dateToLogIdMap={dateToLogIdMap}
        failureOnly
        onClose={activeLogId !== null ? handleLogClose : onClose}
        onAfterDelete={handleAfterDelete}
      />
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-normal text-muted-foreground">
            {headerTitle}
          </DialogTitle>
          <p className="text-base font-semibold">{t('instruments.reports.failures.dialogTitle')}</p>
          {date && <p className="text-sm text-muted-foreground">{date}</p>}
        </DialogHeader>

        {!hasLogs ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('instruments.reports.failures.empty')}
          </p>
        ) : (
          <ul className="space-y-2">
            {instrumentTaskLogIds.map((logId, index) => {
              const timeLabel = failureTimeLabel(logId)
              return (
                <li key={logId} className="rounded-md border">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 p-2 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    onClick={() => handleLogClick(logId)}
                  >
                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="font-medium text-red-600">
                      {t('instruments.reports.logDetails.failure')}
                    </span>
                    {/* REBUILD-554: show the occurrence's completion time so the user can tell
                        which failure this is and open that exact occurrence. Fall back to the
                        ordinal when no timestamp is available. */}
                    {timeLabel ? (
                      <span className="ml-1 text-xs text-muted-foreground">{timeLabel}</span>
                    ) : (
                      <span className="ml-1 text-xs text-muted-foreground">#{index + 1}</span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default InstrumentFailuresDialog
